import React, { useContext } from 'react';
import { useSnackbar } from 'notistack';

import { AuthContext } from '../contexts';

export const HttpMethods = {

    GET: 'GET',
    POST: 'POST',
    DELETE: 'DELETE'
}

export const FetchContext = React.createContext(null);

const { Provider } = FetchContext;
export const FetchProvider = ({ ...props }) => {
    const { getToken } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();

    const snackOptions = { variant: "error", autoHideDuration: 5000 };

    const createError = (code, message) => {
        const error = new Error(message);
        error.code = code;
        return error;
    }

    // source: https://dev.to/ycmjason/javascript-fetch-retry-upon-failure-3p6g
    const fetchRetry = (retries, api, cfg) => {
        return fetch(api, cfg).catch(function (error) {
            if (retries === 1) throw error;
            return fetchRetry(api, cfg, retries - 1);
        });
    }

    const fetchOk = (api, cfg) => {

        var isIE = !!window.MSInputMethodContext && !!document.documentMode;

        const headers = {};
        if (cfg.body && (typeof cfg.body === "string" || cfg.body instanceof String)) {
            headers["Content-Type"] = "application/json"
        }

        if (cfg.headers) {
            for (var pair of cfg.headers.entries()) {
                headers[pair[0]] = pair[1];
            }
        }

        cfg = {
            ...cfg,
            cache: 'no-cache',
            headers: new Headers({
                ...headers,
                "Cache-Control": "no-cache",
                "Pragma": "no-cache",
                "Expires": "0"
            })
        };

        return fetch(api, cfg)
            .then(r => {
                if (r.ok) {
                    return r;
                } else {
                    switch (r.status) {
                        // Bad Request
                        case 400:
                            return r.json()
                                .then(data => {
                                    if (data.message) {
                                        return Promise.reject(createError(r.status, data.message));
                                    } else {
                                        if (data.errors) {
                                            const errorKeys = Object.keys(data.errors);
                                            if (cfg.collectErrorDetails) {
                                                const error = new Error("Dati forniti non validi");
                                                error.details = errorKeys.map(x => ({ errorId: x, message: data.errors[x][0] }));
                                                error.code = r.status;
                                                return Promise.reject(error);
                                            } else {
                                                errorKeys.forEach(errorKey => {
                                                    const error = data.errors[errorKey][0];
                                                    enqueueSnackbar(error, snackOptions);
                                                });
                                            }
                                        }
                                        return Promise.reject(createError(r.status, "Dati forniti non validi"));
                                    }
                                });
                        // Unauthorized
                        case 401:
                            return Promise.reject(createError(r.status, "L'utente non è autorizzato"));
                        // Forbidden
                        case 403:
                            return Promise.reject(createError(r.status, "L'utente non è abilitato"));
                        // Not Found
                        case 404:
                            return Promise.reject(createError(r.status, "Risorsa non trovata"));
                        // Server Error
                        case 500:
                            return r.json()
                                .then(data => {
                                    let message = null;
                                    if (data.message) message = data.message;
                                    else message = "C'è stato un errore nel nostro server";
                                    return Promise.reject(createError(r.status, message));
                                });
                        // Not handled
                        default:
                            return Promise.reject(createError(r.status, "Qualcosa è andato storto"));
                    }
                }
            })
            .catch(error => {
                enqueueSnackbar(error.message, snackOptions);
                return Promise.reject(error);
            });
    }

    const fetchAuthorized = (api, cfg) => {
        const accessToken = getToken();
        return fetchOk(api, {
            ...cfg,
            headers: new Headers({
                "Authorization": `Bearer ${accessToken}`
            })
        })
    }

    // Used for debug purpose
    const delay = (duration) => {
        return new Promise((resolve) => setTimeout(resolve, duration));
    }

    return (
        <Provider value={{
            fetchRetry,
            fetchOk,
            fetchAuthorized
        }}>
            {props.children}
        </Provider>
    );
}